'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { logger } from '@/core/lib/logger';
import { eventBus } from '@/core/lib/event-bus';
import { useAuthContext } from '@/core/providers/AuthProvider';
import { useStaffCallStore, handleNewProducer, setupOutboundMedia } from './useStaffCallClient';
import type { IncomingCallData, CallStatePayload } from '../types/calls.types';

interface SignalingAuth {
  userId: string;
  propertyId: string;
  role?: string;
  dept?: string;
  userType?: 'staff' | 'guest';
  userName?: string;
}

const SIGNALING_URL =
  process.env.NEXT_PUBLIC_SIGNALING_URL ?? 'http://localhost:6201';

/**
 * Notified when a call at this property went unanswered.
 *
 * A callback rather than a direct `useToast()` call because this hook runs
 * inside an effect that must not re-subscribe when a context value changes —
 * re-running it tears down and rebuilds the socket, dropping live audio.
 */
export type MissedCallHandler = (info: {
  callId: string;
  callerName?: string;
  bookingId?: string;
}) => void;

export function useSignalingConnection(
  auth: SignalingAuth | null,
  onMissedCall?: MissedCallHandler,
  /**
   * Called when the socket comes back after a drop.
   *
   * Between the drop and this, the desk was receiving no calls at all — so the
   * staff member needs telling that they are reachable again, not only that
   * something went wrong. Held in a ref for the same reason as
   * `onMissedCall`: depending on its identity would rebuild the socket.
   */
  onReconnected?: () => void,
) {
  const socketRef = useRef<Socket | null>(null);
  const setSocketEmit = useStaffCallStore((s) => s.setSocketEmit);
  const setIncoming = useStaffCallStore((s) => s.setIncoming);
  const userId = auth?.userId;
  const propertyId = auth?.propertyId;

  // Held in a ref so the socket effect does not depend on the callback's
  // identity. A parent that re-renders would otherwise reconnect the socket
  // mid-call, dropping live audio.
  //
  // Assigned in an effect rather than during render: writing a ref while
  // rendering is a side effect React may discard or replay under concurrent
  // rendering, and it is exactly what `react-hooks/refs` flags.
  // Whether the socket has dropped since it last connected. A ref, not state:
  // it must not re-run the socket effect, which would tear down and rebuild
  // the connection — dropping live audio to report that audio had dropped.
  const disconnectedRef = useRef(false);
  const reconnectedRef = useRef<(() => void) | undefined>(undefined);

  const missedCallRef = useRef<MissedCallHandler | undefined>(onMissedCall);
  useEffect(() => {
    missedCallRef.current = onMissedCall;
  }, [onMissedCall]);

  useEffect(() => {
    reconnectedRef.current = onReconnected;
  }, [onReconnected]);

  useEffect(() => {
    if (!userId || !propertyId) return;

    // The server derives userId / propertyId / role from this token and
    // ignores whatever else is in `auth` — sending them was how an
    // unauthenticated client could previously join any property's room.
    // A legacy pre-migration token, if this browser still has one. Current
    // sessions have nothing here by design — the credential is an HttpOnly
    // cookie the socket sends via `withCredentials` below.
    //
    // This used to RETURN when the token was absent, so after the cookie
    // migration the socket was never created at all and calling was silently
    // dead for every real session. Measured in the client error log: 51
    // "[signaling] no access token — not connecting" warnings in one day, and
    // not one successful connection from a cookie session.
    //
    // Absence of a readable token is not absence of a credential, so it is no
    // longer a reason to skip connecting. If the cookie is missing or invalid
    // the server rejects the handshake and `connect_error` reports it — a real
    // signal, rather than this client-side guess.
    let token: string | null = null;
    try {
      token = typeof window !== 'undefined' ? localStorage.getItem('access_token') : null;
    } catch {
      token = null;
    }

    const socket = io(SIGNALING_URL, {
      // Token only. Spreading `auth` sent fields the server discards, and
      // made the effect depend on an object identity that changes on every
      // render of the caller — which would tear down and rebuild the socket
      // mid-call, dropping the staff member's audio, purely because a parent
      // re-rendered.
      auth: token ? { token } : {},
      transports: ['websocket'],
      // Send the HttpOnly auth cookie on the upgrade request.
      //
      // Once the client stops writing tokens to localStorage, `token` above is
      // null and the cookie becomes the ONLY credential the socket can present:
      // a browser cannot set headers on a WebSocket, and it cannot read an
      // HttpOnly cookie to place it in `auth`. Without this the socket connects
      // with no credential and is rejected, so calling would break silently
      // while every HTTP route kept working.
      withCredentials: true,
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1_000,
      reconnectionDelayMax: 5_000,
    });
    socketRef.current = socket;

    const emit: Parameters<typeof setSocketEmit>[0] = (event, data, cb) => {
      socket.emit(event, data, cb as (res: Record<string, unknown>) => void);
    };
    setSocketEmit(emit);

    socket.on('call:incoming', (payload: Omit<IncomingCallData, 'origin'>) => {
      setIncoming({ ...payload, origin: 'socket' });
    });

    socket.on(
      'call:answer',
      (payload: {
        callId: string;
        consumerParams?: Record<string, unknown>;
        producerParams?: Record<string, unknown>;
        staffName?: string;
      }) => {
        if (payload.producerParams && userId) {
          handleNewProducer(payload.callId, userId, emit);
        }

        // OUTBOUND: this staff member placed the call and the guest just
        // answered.
        //
        // On an INBOUND call `acceptCall` already negotiated media — it runs
        // when this staff member presses Accept, which is the moment the room
        // exists. Outbound reaches that moment here instead: the room is
        // created when the GUEST accepts, so there is nothing to negotiate
        // against until this event arrives. Without it the call shows as
        // connected on both sides and carries no audio in either direction,
        // because this side never created a transport at all.
        const store = useStaffCallStore.getState();
        const active = store.activeCall;
        if (active && active.callId === payload.callId && store.state === 'connecting') {
          setupOutboundMedia(payload.callId, emit, payload.staffName);
        }
      }
    );

    // The gateway refused the call, or it could not be placed.
    //
    // Outbound has refusal reasons inbound does not — the guest is offline, is
    // already on a call, or has no booking with this hotel. Each is actionable
    // (fall back to the phone number on the booking), and without this handler
    // the staff member's screen sits on "Connecting…" until they navigate away.
    socket.on('call:rejected', (payload: { callId?: string; reason?: string; message?: string }) => {
      const store = useStaffCallStore.getState();
      if (store.activeCall && payload.callId && store.activeCall.callId !== payload.callId) {
        return;
      }
      store.setOutboundFailed(
        payload.message ?? 'The call could not be placed. Please try again.',
      );
    });

    // `media:new-producer`, NOT `call:new-producer`.
    //
    // The gateway emits this under the `media:` prefix (call-gateway.ts
    // `notifyNewProducer`). Listening on `call:new-producer` meant the handler
    // never fired, so `consumeRemoteAudio` was never called and the staff
    // member never subscribed to the guest's audio track: every call connected
    // and then sat in silence, with nothing logged as wrong on either side.
    socket.on(
      'media:new-producer',
      (payload: { callId: string; producerUserId: string }) => {
        handleNewProducer(payload.callId, payload.producerUserId, emit);
      }
    );

    socket.on('call:ended', (payload: { callId: string; status?: string; reason?: string }) => {
      const store = useStaffCallStore.getState();

      // Surface WHY, not just that it ended.
      //
      // The gateway has always sent a reason — "declined", "No answer within
      // timeout", a media failure — and this handler logged it and threw it
      // away. On an outbound call that is the difference between a staff
      // member knowing the guest declined (so: phone the number on the
      // booking) and watching the panel close for no stated reason.
      //
      // DIRECTION decides the wording, because the same server status means
      // opposite things: `declined` outbound is "the guest refused us";
      // inbound it is "we refused them", which the staff member just did
      // deliberately and does not need reporting back as a failure.
      //
      // Only the outbound cases are surfaced. An inbound decline or timeout is
      // already visible — the panel clears, and a missed call raises its own
      // toast — so repeating it would report the staff member's own action to
      // them as a problem.
      const outbound = store.activeCall?.direction === 'outbound';

      if (outbound && payload.status === 'declined') {
        store.setOutboundFailed(payload.reason ?? 'The guest declined the call.');
      } else if (outbound && payload.status === 'abandoned') {
        store.setOutboundFailed('No answer. Try the phone number on the booking.');
      } else {
        store.endCall();
      }

      if (payload?.reason) {
        logger.info('[signaling] call ended', {
          metadata: { callId: payload.callId, status: payload.status, reason: payload.reason },
        });
      }
    });

    // ---- Events the server emits that the UI previously ignored -----------
    //
    // The gateway emits thirteen events; this client handled two. The rest
    // were defined in `core/constants/socket-events.ts` but never bound, so
    // the screen silently missed every state change below.

    // Someone else answered first. Without this the incoming-call panel stays
    // on screen ringing a call that is already being handled, and the staff
    // member presses Accept only to be told it was taken.
    socket.on('call:already-taken', (payload: { callId: string }) => {
      useStaffCallStore.getState().clearIncoming(payload.callId);
    });

    // Broadcast to the whole property when a call is claimed — dismisses the
    // ringing panel on every OTHER staff member's screen.
    socket.on(
      'call:picked-up',
      (payload: { callId: string; byStaffId: string; byStaffName?: string }) => {
        if (payload.byStaffId === userId) return; // we are the one who took it
        useStaffCallStore.getState().clearIncoming(payload.callId);
      },
    );

    // Admission refused (instance at capacity) — the caller must be told,
    // otherwise the dialling screen waits forever.
    socket.on('call:rejected', (payload: { callId: string; reason?: string; message?: string }) => {
      useStaffCallStore.getState().endCall();
      logger.warn('[signaling] call rejected', {
        metadata: { callId: payload.callId, reason: payload.reason },
      });
    });

    // Media/room failure after accept. Same treatment: end the local call
    // rather than leaving a dead "connected" screen.
    socket.on('call:error', (payload: { callId: string; reason?: string }) => {
      useStaffCallStore.getState().endCall();
      logger.error('[signaling] call error', {
        metadata: { callId: payload.callId, reason: payload.reason },
      });
    });

    // Property-wide ringing/queue state, used for the front-desk call bar.
    // `call:state` carries TWO different payloads under one event name.
    //
    //  - The property-wide queue snapshot (`{ ringing, queueDepth }`), which
    //    every socket at the property receives.
    //  - A per-call acknowledgement (`{ callId, status: 'dialing' }`), sent
    //    only to the socket that just placed an OUTBOUND call.
    //
    // The second one matters because the gateway REPLACES a client-generated
    // callId when it collides with a live call, and this acknowledgement is
    // where it reports the id it actually used. Feeding it to `setCallState`
    // (which expects the queue shape) discarded it, so on a collision the staff
    // client held a callId the server had never heard of: Hang up, mute and
    // transfer would all target a call that does not exist, and the real one
    // could only be released by its 60-second ring deadline.
    socket.on('call:state', (payload: CallStatePayload | { callId: string; status: string }) => {
      if ('callId' in payload && typeof payload.callId === 'string') {
        useStaffCallStore.getState().adoptCallId(payload.callId);
        return;
      }
      useStaffCallStore.getState().setCallState(payload as CallStatePayload);
    });

    // The gateway confirmed a recorder is running on this call.
    //
    // Emitted only once ffmpeg is actually up, so the badge reflects reality
    // rather than a policy that permits recording. Both parties are told —
    // consent stamped in a database column is not notice to the person on the
    // call, which is the GDPR/CCPA exposure this closes.
    socket.on('call:recording-started', (payload: { callId?: string }) => {
      // Pass the callId through: the store refuses a badge meant for a
      // different call, which a late-arriving event can be.
      useStaffCallStore.getState().markRecording(payload?.callId);
    });

    // A transfer this staff member initiated was accepted by the gateway.
    socket.on('call:transferred', (payload: { callId: string; toStaffName?: string }) => {
      useStaffCallStore.getState().endCall();
      logger.info('[signaling] call transferred', {
        metadata: { callId: payload.callId, to: payload.toStaffName },
      });
    });

    // Transfer target unavailable — the call is still OURS, so the active-call
    // screen must stay up rather than being torn down.
    socket.on('call:transfer-failed', (payload: { callId: string; reason?: string }) => {
      useStaffCallStore.getState().setTransferFailed(payload.reason ?? 'Transfer failed');
    });

    // A call rang out unanswered anywhere at this property.
    //
    // The gateway broadcast this to `property:<id>` and nothing listened. The
    // socket-contract test even exempted it as "handled by the notifications
    // surface" — an exemption that was simply not true, which is why an
    // exemption list has to be checked rather than trusted. A missed call is
    // the only signal the front desk gets that someone tried to reach them and
    // gave up.
    socket.on(
      'call:missed-new',
      (payload: { callId: string; callerName?: string; bookingId?: string }) => {
        missedCallRef.current?.(payload);
      },
    );

    // The socket dropped.
    //
    // There was no handler at all, so a staff member whose connection died
    // learned nothing: the incoming-call panel simply stopped appearing, and
    // any live call went silent while the screen kept showing "connected".
    // From the desk it looks like the hotel has stopped receiving calls for no
    // reason — and the guest on the other end is told the connection was lost
    // while the staff member is told nothing at all.
    //
    // A deliberate client-side disconnect is our own teardown (unmount, sign
    // out) and needs no alarm.
    socket.on('disconnect', (reason: string) => {
      if (reason === 'io client disconnect') return;

      const store = useStaffCallStore.getState();
      logger.warn('[signaling] disconnected', { metadata: { reason } });

      // A call in flight is over — the media transports were bound to the
      // socket that just died, so even an immediate reconnect cannot resume
      // it. Say so, rather than leaving a dead call on screen.
      if (store.state !== 'idle' && store.state !== 'ended') {
        // `setOutboundFailed` is the store's "clear the call and record why"
        // action; the name reflects where it was introduced, not its scope. A
        // dropped socket ends an INBOUND call just as finally — the media
        // transports were bound to the socket that died, so even an immediate
        // reconnect cannot resume it — and the staff member needs telling
        // either way.
        store.setOutboundFailed(
          'The connection to the call server was lost. The call has ended.',
        );
      }

      disconnectedRef.current = true;
    });

    // Reconnected. socket.io retries on its own, so this is the point at which
    // the desk starts receiving calls again — and the staff member needs to
    // know, because between the two events they were unreachable.
    socket.on('connect', () => {
      if (!disconnectedRef.current) return;
      disconnectedRef.current = false;
      logger.info('[signaling] reconnected');
      reconnectedRef.current?.();
      // Anything relayed while we were away was missed: boards refetch.
      eventBus.emit('live:resync', {});
    });

    // Operational events (housekeeping tasks, room status) ride the same
    // authenticated staff socket rather than a second connection. They are
    // forwarded to the in-page event bus, where `useLiveOperations` turns
    // each into a narrow query invalidation.
    socket.onAny((event: string, payload: unknown) => {
      if (event.startsWith('hk:') || event.startsWith('room:')) eventBus.emit(event, payload);
    });

    socket.on('connect_error', (err) => {
      // Goes to the log endpoint rather than the console: a staff member whose
      // socket never connects gets no incoming-call alerts, and that failure
      // has to be visible in the error log rather than only in a devtools
      // window nobody has open.
      //
      // One rejection is EXPECTED and is not a failure: a session with no
      // property scope — a platform admin on an /admin route, a chain owner
      // above the property level — cannot join a property room, and the server
      // says so.
      // Those users have no calling feature to lose, so warning about it
      // reports correct authorisation as a problem and buries the real case
      // (a property-scoped staff member who genuinely cannot connect) among
      // noise. Logged at info so the event is still traceable.
      const isExpectedScopeRejection = err.message.includes('not scoped to a property');

      if (isExpectedScopeRejection) {
        logger.info('[signaling] not connecting — session has no property scope', {
          metadata: { message: err.message },
        });
        return;
      }

      logger.warn('[signaling] connect_error', { metadata: { message: err.message } });
    });

    // Re-check the connection when the tab returns to the foreground.
    //
    // A front desk switches tabs constantly — to a reservation, to email, to
    // the PMS — and browsers may drop a background socket outright. socket.io
    // only discovers that on its next ping, so between the drop and that ping
    // the desk silently receives NO incoming calls while the app looks fine.
    // That is the worst shape this failure can take: nothing on screen is
    // wrong, and calls are being missed.
    //
    // `connect()` is a no-op on a live socket, so this costs nothing in the
    // normal case and repairs the abnormal one immediately.
    const onVisible = () => {
      if (typeof document === 'undefined' || document.visibilityState !== 'visible') return;
      if (!socket.connected) socket.connect();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
      setSocketEmit(null as unknown as Parameters<typeof setSocketEmit>[0]);
    };
    // Primitives only. `auth?.role` was in this list but is no longer read by
    // the effect (the server derives it from the token), and depending on it
    // would reconnect the socket — dropping live audio — on a role change the
    // connection does not care about.
  }, [userId, propertyId, setSocketEmit, setIncoming]);
}

/**
 * Read the cached signalling identity from sessionStorage.
 *
 * Used as a lazy `useState` initialiser rather than synced from an effect:
 * sessionStorage is synchronously readable, so the effect only added a render
 * pass in which `auth` was null — and during that pass `useSignalingConnection`
 * saw no identity and skipped connecting, so every mount deferred the socket by
 * one full render.
 */
function readCachedAuth(): SignalingAuth | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = sessionStorage.getItem('staff-signaling-auth');
    if (!stored) return null;
    return JSON.parse(stored) as SignalingAuth;
  } catch {
    // Corrupt cache or storage blocked — fall through to the manual load.
    return null;
  }
}

export function useStaffSignalingConnection(): SignalingAuth | null {
  const [auth, setAuth] = useState<SignalingAuth | null>(readCachedAuth);

  // Whether this session belongs to a hotel at all. See the effect below for
  // why the request must not be made without one.
  const { user } = useAuthContext();
  const propertyId = user?.propertyId;

  // Fetch the identity when there is no cached one.
  //
  // This used to live in `IncomingCallHost`, which called
  // `loadStaffSignalingAuth()` and DISCARDED the promise: the identity was
  // written to sessionStorage and nothing re-read it until the next full page
  // load. A staff member on their first visit of a session therefore never
  // connected to the signalling server at all — no incoming call reached them,
  // and nothing logged an error, because from the app's point of view there
  // was simply no identity to connect with.
  //
  // `cancelled` guards the unmount race: without it a resolved fetch calls
  // setState on a torn-down component.
  useEffect(() => {
    if (auth) return;

    // Only a session with a PROPERTY can be hotel staff.
    //
    // /api/v1/staff/me goes through the staff guard, which answers 403 for a
    // platform or chain user. `loadStaffSignalingAuth` swallows that and
    // resolves null, so the feature behaved correctly — but api-client
    // classifies an authenticated 403 as a fault and writes it to error.log, so
    // every super-admin page load logged one and printed
    // `GET /api/v1/staff/me 403 (Forbidden)` in the console.
    //
    // A platform admin is not staff of any hotel and cannot receive a call, so
    // there was never an identity to load here.
    if (!propertyId) return;

    let cancelled = false;
    loadStaffSignalingAuth()
      .then((loaded) => {
        if (!cancelled && loaded) setAuth(loaded);
      })
      .catch(() => {
        // `loadStaffSignalingAuth` already swallows its errors and resolves
        // null; this is belt-and-braces so an unexpected throw cannot become
        // an unhandled rejection.
      });

    return () => {
      cancelled = true;
    };
  }, [auth, propertyId]);

  return auth;
}

export async function loadStaffSignalingAuth(): Promise<SignalingAuth | null> {
  try {
    const res = await fetch('/api/v1/staff/me', { credentials: 'include' });
    if (!res.ok) return null;
    const json = await res.json() as { data?: { staffId: string; propertyId: string; role?: string } };
    if (!json?.data) return null;
    const payload: SignalingAuth = {
      userId: json.data.staffId,
      propertyId: json.data.propertyId,
      role: json.data.role,
      userType: 'staff',
    };
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('staff-signaling-auth', JSON.stringify(payload));
    }
    return payload;
  } catch {
    return null;
  }
}

export function getSignalingSocket(): Socket | null {
  return null;
}
